import {AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, Input, OnInit, ViewChild} from '@angular/core';
import {LoadableSelectorContainer, TableDefinition} from '@aviary/components/table/table.component.types';
import {createSelector, Store} from '@ngrx/store';
import {RouterSelectors} from '@aviary/store/router/router.selectors';
import {RouterActions} from '@aviary/store/router/router.actions';
import {PaginationSelectors} from '@aviary/store/pagination/pagination.selectors';
import {TableCardHeaderFilterDefinitions} from '@aviary/components/table-card/table-card-header-filters/table-card-header-filter.types';
import {TableCardHeaderActionDefinitions} from '@aviary/components/table-card/table-card-header-actions/table-card-header-action.types';
import {LeftHeaderContent, LeftHeaderText, PrintableTableDefinition, TableCardDefinition} from '@aviary/components/table-card/table-card.component.types';
import {MxSelector} from '@aviary/store/store.types';
import {isDefined, isPopulatedArray, isPopulatedString, isString} from '@aviary/store/common/typing.helpers';
import {PrintService} from '@aviary/services/print.service';
import {PrintableTableComponent} from '@aviary/components/table/printable-table/printable-table.component';
import {MatTooltip} from '@angular/material/tooltip';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SelectOptions} from '@aviary/store/common/common.types';
import {buildFormGroup, getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {snapshot} from '@aviary/store/store.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {MatCard, MatCardContent} from '@angular/material/card';
import {NgTemplateOutlet} from '@angular/common';
import {SelectFormFieldComponent} from '@aviary/forms/fields/select-form-field/select-form-field.component';
import {MatDivider} from '@angular/material/divider';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {ActionButtonsComponent} from '@aviary/components/action-buttons/action-buttons.component';
import {TableCardHeaderFiltersComponent} from '@aviary/components/table-card/table-card-header-filters/table-card-header-filters.component';
import {TableCardHeaderActionsComponent} from '@aviary/components/table-card/table-card-header-actions/table-card-header-actions.component';
import {TableComponent} from '@aviary/components/table/table.component';
import {MatFormField} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatIconButton} from '@angular/material/button';
import {MatInput} from '@angular/material/input';
import {FormatPipe} from '@aviary/pipes/format.pipe';
import {alwaysFalse} from '@aviary/store/common/common.selectors';
import {PreventFocusDirective} from '@aviary/directives/prevent-focus.directive';

const DEFAULT_LEFT_HEADER_TEXT_CLASSNAME = 'mx-table-card-left-header-text';

@Component({
  selector: 'mx-table-card',
  templateUrl: './table-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatCard, MatCardContent, NgTemplateOutlet, SelectFormFieldComponent, MatDivider, SelectPipe, ActionButtonsComponent, TableCardHeaderFiltersComponent,
    TableCardHeaderActionsComponent, TableComponent, MatFormField, MatIcon, MatIconButton, MatInput, MatTooltip, PreventFocusDirective
  ]
})
export class TableCardComponent<ObjectType, DefaultSelectorContainer extends LoadableSelectorContainer> implements OnInit, AfterViewInit {

  @ViewChild('searchInput')
  public set searchInputElementRef(elementRef: ElementRef) {
    this.searchInputElement = elementRef?.nativeElement;
  }

  @Input()
  public set definition(definition: TableCardDefinition<ObjectType, DefaultSelectorContainer>) {
    this.leftHeaderContent = definition.leftHeaderContent ?? 'SEARCH';
    this.tableDefinition = definition.table;
    if (!isString(this.leftHeaderContent)) this.leftHeaderText = this.createInternalLeftHeaderTextFor(this.leftHeaderContent);
    this.filterDefinitions = definition.filters ?? [];
    this.actionDefinitions = definition.actions ?? [];
    this.searchableAttributes = definition.searchableAttributes ?? [];
    this.isSelectSearch = definition.leftHeaderContent === 'SELECT_SEARCH';
    this.isMultiSearch = this.searchableAttributes.length > 1;
    this.searchSelectPlaceholder = this.searchableAttributes.length !== 1 ? 'SEARCH' : `Search ${this.searchableAttributes[0].displayName}`;

    this.printDefinition = definition.printableTable;

    const dataSourceSelector: MxSelector<ObjectType[]> =
      isString(definition.table.dataSource)
        ? definition.table.defaultSelectorContainer[String(definition.table.dataSource)]
        : definition.table.dataSource;

    this.enablePrintButton = createSelector(
      dataSourceSelector,
      isPopulatedArray
    );
  }

  public leftHeaderContent: LeftHeaderContent;
  public leftHeaderText: InternalLeftHeaderText;
  public filterDefinitions: TableCardHeaderFilterDefinitions;
  public actionDefinitions: TableCardHeaderActionDefinitions;
  public searchableAttributes: SelectOptions<string>;
  public isMultiSearch: boolean;
  public tableDefinition: TableDefinition<ObjectType, DefaultSelectorContainer>;
  public searchSelectPlaceholder: string;
  public printDefinition?: PrintableTableDefinition;
  public enablePrintButton: MxSelector<boolean> = alwaysFalse;

  public readonly routerSelectors = RouterSelectors;
  public readonly searchQueryParamName = 'search';
  public readonly searchSelectParamName = 'searchTarget';
  public showSearchClearButton = false;

  // Form Group simply manages the searchSelect input, if there is one.
  public readonly formGroup: ExtendedFormGroup = buildFormGroup({searchSelect: ''}, {searchSelect: []});
  private searchInputElement: HTMLInputElement;
  private isSelectSearch: boolean;
  private readonly searchSelectControl: ExtendedFormControl = getExtendedControlFrom(this.formGroup, 'searchSelect');
  private readonly store: Store = inject(Store);
  private readonly elementRef: ElementRef = inject(ElementRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly formatPipe: FormatPipe = inject(FormatPipe);
  private readonly printService: PrintService = inject(PrintService);

  constructor() {
    this.searchSelectControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(value => {
        this.onChangeSearchSelect(value);
      });
  }

  ngOnInit() {
    if (this.isSelectSearch && this.searchableAttributes.length > 0) {
      // If we have searchableAttributes, set the select value to the current selected query param, or otherwise the first option in the list.
      const currentSelectValue = snapshot(this.routerSelectors.queryParamValue(this.searchSelectParamName), this.store);
      this.searchSelectControl.setValue(isDefined(currentSelectValue) ? currentSelectValue : this.searchableAttributes[0].value, {emitEvent: false});
    }
  }

  public ngAfterViewInit(): void {
    // Wait until the next browser cycle before searching for elements to enable/disable -- we're generating some elements, and they're not immediately ready.
    setTimeout(() => this.initializeElementEnablementChangers());
    this.addOrRemoveSearchClearButton();
  }

  public onInputInSearchBox(): void {
    this.addOrRemoveSearchClearButton();
  }

  public onKeydownEnterInSearchBox(): void {
    if (document.activeElement !== this.searchInputElement) return;

    const searchInputValue: string = this.searchInputElement.value.trim();
    if (searchInputValue) {
      this.store.dispatch(this.isSelectSearch ?
        RouterActions.updateSearchTermAndTargetParams(searchInputValue, this.searchSelectControl.value) :
        RouterActions.addFilterParam(this.searchQueryParamName, searchInputValue)
      );
    } else {
      this.clearSearchParams();
    }
  }

  public onClickSearchClearButton(): void {
    this.searchInputElement.value = '';
    this.clearSearchParams();
    this.showSearchClearButton = false;
  }

  public onClickPrintButton(): void {
    this.printService.print(
      PrintableTableComponent,
      {
        tableDefinition: this.tableDefinition,
        printDefinition: this.printDefinition,
        filterDefinitions: this.filterDefinitions
      }
    );
  }

  public onChangeSearchSelect(selectValue: string): void {
    if (isDefined(this.searchInputElement)) {
      const trimmedInputValue = this.searchInputElement.value.trim();
      if (isPopulatedString(trimmedInputValue)) {
        // Only add/update the searchTarget param if there is also a search value, primarily so the url isn't unnecessarily cluttered
        this.store.dispatch(RouterActions.updateSearchTermAndTargetParams(trimmedInputValue, selectValue));
      }
    }
  }

  private initializeElementEnablementChangers(): void {
    const nativeElement: HTMLElement = this.elementRef.nativeElement;

    if (this.tableDefinition.includePaginationControls ?? true) {
      const elementChanges: ElementChangeItem[] =
        ENABLEMENT_CHANGE_DEFINITIONS.flatMap(definition => {
          const elements: HTMLElement[] = Array.from(nativeElement.querySelectorAll(definition.querySelector));

          return elements.map(element => ({
            enable: () => definition.enable(element),
            disable: () => definition.disable(element)
          }))
        });

      this.store.select(PaginationSelectors.hasData)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(hasData => {
          for (const elementChangeItem of elementChanges) {
            elementChangeItem[hasData ? 'enable' : 'disable']();
          }
        });
    }
  }

  private addOrRemoveSearchClearButton(): void {
    this.showSearchClearButton = this.searchInputElement?.value.length > 0;
  }

  private createInternalLeftHeaderTextFor(leftHeaderText: LeftHeaderText): InternalLeftHeaderText {
    return {
      valueSelector: this.createSelectorFor(leftHeaderText.value, leftHeaderText.count),
      cssClasses: leftHeaderText.cssClasses ?? DEFAULT_LEFT_HEADER_TEXT_CLASSNAME
    };
  }

  private createSelectorFor(headerText: string | MxSelector<string>, countSelector?: MxSelector<number>): MxSelector<string> {
    const textSelector: MxSelector<string> = isString(headerText) ? createSelector(() => headerText) : headerText;

    return isDefined(countSelector)
      ? createSelector(
        textSelector,
        countSelector,
        this.tableDefinition.defaultSelectorContainer.awaitingAnyLoad,
        (text, count, awaitingLoad) => awaitingLoad ? text : `${text} (${this.formatPipe.transform(count, 'NUMBER') ?? '0'})`
      )
      : textSelector;
  }

  private clearSearchParams(): void {
    this.store.dispatch(this.isSelectSearch ? RouterActions.removeSearchTermAndTargetParams() : RouterActions.removeFilterParam(this.searchQueryParamName));
  }
}

// If there are any new elements that can appear above the table in a table card, add them here, along with functions to enable/disable styles and mouse events.
const ENABLEMENT_CHANGE_DEFINITIONS: EnablementChangeDefinition[] = [
  {
    // Search Input, visible for both SEARCH and SELECT_SEARCH contexts
    querySelector: '.mx-search-box',
    enable: element => {
      element.classList.remove('pointer-events-none');
      element.querySelector('input')?.removeAttribute('disabled');
      element.querySelector('.mat-icon')?.classList.remove('opacity-40');
    },
    disable: element => {
      const inputElement: HTMLInputElement | null = element.querySelector('input');
      if (inputElement?.value) return;  // Don't want to disable the search box if it's in use, because it's possible that the last search emptied the table.

      element.classList.add('pointer-events-none');
      inputElement?.setAttribute('disabled', 'true');
      element.querySelector('.mat-icon')?.classList.add('opacity-40');
    }
  },
  {
    // Search select dropdown, visible only for SELECT_SEARCH context when there is more than one searchable attribute.
    querySelector: '.mx-search-select mat-form-field',
    enable: element => {
      if (isDefined(element)) {
        element.classList.remove('pointer-events-none', 'mat-form-field-disabled');
        element.querySelector('input')?.removeAttribute('disabled');
        element.querySelector('.mat-icon')?.classList.remove('opacity-40');
      }
    },
    disable: element => {
      if (isDefined(element)) {
        const inputElement: HTMLInputElement | null = element.querySelector('input');
        const queryInputElement: HTMLInputElement | null = document.querySelector('.mx-search-box input');
        if (queryInputElement?.value) return;  // Don't disable the dropdown if the query input has any value

        element.classList.add('pointer-events-none', 'mat-form-field-disabled');
        inputElement?.setAttribute('disabled', 'true');
        element.querySelector('.mat-icon')?.classList.add('opacity-40');
      }
    }
  },
  {
    // Search select container, visible only for SELECT_SEARCH context when there is more than one searchable attribute.
    querySelector: '.mx-select-search-input',
    enable: element => {
      if (isDefined(element)) {
        element.classList.remove('border-outline-variant');
      }
    },
    disable: element => {
      if (isDefined(element)) {
        const queryInputElement: HTMLInputElement | null = document.querySelector('.mx-search-box input');
        if (queryInputElement?.value) return;  // Don't disable the dropdown if the query input has any value
        element.classList.add('border-outline-variant');
      }
    }
  }
];

interface EnablementChangeDefinition {
  querySelector: string;
  enable: (element: HTMLElement) => void;
  disable: (element: HTMLElement) => void;
}

interface ElementChangeItem {
  enable: () => void;
  disable: () => void;
}

type InternalLeftHeaderText = Required<Omit<LeftHeaderText, 'value' | 'count'>> & { valueSelector: MxSelector<string> };
